// components/MembersSection.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Grid3x3, LayoutGrid } from 'lucide-react';

const SkeletonLoader = ({ count = 6 }) => (
  <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="bg-white rounded-lg shadow-lg overflow-hidden animate-pulse">
        <div className="w-full h-64 bg-gray-200"></div>
        <div className="p-4">
          <div className="h-4 bg-gray-200 rounded mb-2"></div>
          <div className="h-3 bg-gray-200 rounded w-3/4"></div>
        </div>
      </div>
    ))}
  </div>
);

export default function MembersSection({ members, loading }) {
  const [displayStyle, setDisplayStyle] = useState('style1');

  // Style 1: Image focused with overlay bio
  const Style1 = () => (
    <div className="grid gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
      {members.map((member, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="group bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition"
        >
          <div className="relative h-72 overflow-hidden bg-gray-200">
            <img
              src={member.image || `https://placehold.co/400x500?text=${member.name}`}
              alt={member.name}
              className="w-full h-full object-cover object-top group-hover:scale-105 transition duration-300"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent opacity-0 group-hover:opacity-100 transition duration-300 flex flex-col justify-end p-4">
              <p className="text-white text-sm line-clamp-3">{member.bio}</p>
            </div>
          </div>

          <div className="p-4 text-center">
            <h3 className="text-xl font-semibold text-slate-800">{member.name}</h3>
            <p className="text-purple-600 font-medium text-sm">{member.role}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );

  // Style 2: Side by side with image on left
  const Style2 = () => (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
      {members.map((member, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition"
        >
          <div className="flex flex-col sm:flex-row">
            <div className="h-48 sm:h-auto sm:w-1/3 overflow-hidden bg-gray-200 flex-shrink-0">
              <img
                src={member.image || `https://placehold.co/400x500?text=${member.name}`}
                alt={member.name}
                className="w-full h-full object-cover object-top hover:scale-110 transition duration-300"
              />
            </div>
            <div className="p-4 sm:p-6 flex-1 flex flex-col justify-center">
              <h3 className="text-lg font-semibold text-slate-800 mb-1">{member.name}</h3>
              <p className="text-purple-600 font-medium text-sm mb-3">{member.role}</p>
              <p className="text-gray-600 text-sm line-clamp-3">{member.bio}</p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );

  // Style 3: Large card with full info visible
  const Style3 = () => (
    <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
      {members.map((member, index) => (
        <motion.div
          key={index}
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: index * 0.1 }}
          className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-2xl transition"
        >
          <div className="h-80 overflow-hidden bg-gray-200">
            <img
              src={member.image || `https://placehold.co/400x500?text=${member.name}`}
              alt={member.name}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <div className="p-6">
            <h3 className="text-2xl font-semibold text-slate-800 mb-2">{member.name}</h3>
            <p className="text-purple-600 font-medium mb-4">{member.role}</p>
            <p className="text-gray-600 text-sm leading-relaxed">{member.bio}</p>
          </div>
        </motion.div>
      ))}
    </div>
  );

  return (
    <section id="members" className="w-full min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pt-24">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
        </motion.div>

        {/* Display Style Toggle */}
        {!loading && members.length > 0 && (
          <div className="flex justify-center gap-3 mb-8">
            <button
              onClick={() => setDisplayStyle('style1')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                displayStyle === 'style1'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Grid3x3 size={18} /> Grid
            </button>
            <button
              onClick={() => setDisplayStyle('style2')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                displayStyle === 'style2'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid size={18} /> Horizontal
            </button>
            <button
              onClick={() => setDisplayStyle('style3')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition ${
                displayStyle === 'style3'
                  ? 'bg-purple-600 text-white shadow-lg'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
              }`}
            >
              <LayoutGrid size={18} /> Large
            </button>
          </div>
        )}

        {loading ? (
          <SkeletonLoader count={6} />
        ) : members.length === 0 ? (
          <p className="text-center text-gray-500 text-lg">No members added yet</p>
        ) : (
          <>
            {displayStyle === 'style1' && <Style1 />}
            {displayStyle === 'style2' && <Style2 />}
            {displayStyle === 'style3' && <Style3 />}
          </>
        )}
      </div>
    </section>
  );
}